/**
 * TrendDetailPage — Trend collection overview page
 *
 * Route: /trend/:trendSlug
 *
 * PAGE STRUCTURE:
 *   1. TrendHero    — full-screen cinematic hero
 *   2. TrendIntro   — themes + editorial body text + quote
 *   3. TrendSection — repeating section(s) with TrendCardGrid
 *   4. 404 fallback — "coming soon" if slug not in trendData
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CategoryCarousel } from "./components/CategoryCarousel";
import { CategoryTabs } from "./components/CategoryTabs";
import { TrendCardGrid } from "./components/TrendCardGrid";
import { TrendHero } from "./components/TrendHero";
import { TrendIntro } from "./components/TrendIntro";
import { TrendSection } from "./components/TrendSection";
import { trendData } from "./trendData";
import "./TrendDetailPage.css";

export const TrendDetailPage = () => {
  const { trendSlug } = useParams<{ trendSlug: string }>();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState(() => {
    const s = Number(searchParams.get("section"));
    return Number.isFinite(s) && s >= 0 ? s : 0;
  });

  const handleSectionChange = (index: number) => {
    setActiveSection(index);
    setSearchParams(index > 0 ? { section: String(index) } : {}, {
      replace: true,
    });
  };

  const entry = trendSlug ? trendData[trendSlug] : undefined;

  // ── 404 fallback ──────────────────────────────────────────────────────────
  if (!entry) {
    return (
      <div className="trend-detail-page trend-detail-page__not-found">
        <div className="trend-detail-page__not-found-accent" />
        <p className="trend-detail-page__not-found-title">
          {t("trend_coming_soon", "Detail page coming soon")}
        </p>
        <p className="trend-detail-page__not-found-desc">
          {t(
            "trend_coming_soon_desc",
            "This collection is being curated. Visit the homepage to explore available trend reports.",
          )}
        </p>
        <Link to="/" className="trend-detail-page__not-found-link">
          {t("common_back_home", "← Back to homepage")}
        </Link>
      </div>
    );
  }

  return (
    <div className="trend-detail-page">
      {/* Breadcrumb */}
      <nav
        className="trend-card-detail-page__breadcrumb"
        aria-label="Breadcrumb"
      >
        <Link
          to="/"
          className="trend-card-detail-page__crumb trend-card-detail-page__crumb--home"
        >
          <svg
            width="12"
            height="15"
            viewBox="0 0 12 14.414"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M1 5.207 6 1l5 4.207v7.207a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V5.207Z"
              stroke="currentColor"
              strokeWidth="1.2"
            />
          </svg>
          {t("common_homepage", "Homepage")}
        </Link>
        <span className="trend-card-detail-page__crumb-sep" aria-hidden="true">
          /
        </span>
        <span className="trend-card-detail-page__crumb trend-card-detail-page__crumb--active">
          {entry.title}
        </span>
      </nav>

      {/* 1. Cinematic hero */}
      <TrendHero
        heroImage={entry.heroImage}
        title={entry.title}
        chip={entry.heroChip ?? t("trend_chip_label", "Design Trend Report")}
        description={entry.heroDescription ?? entry.introText[0]}
        exploreLabel={entry.heroExploreLabel}
        showExplore
        truncate
      />

      {/* 1.5 Category carousel — only when multiple sections */}
      {entry.sections.length > 1 && (
        <CategoryCarousel
          sections={entry.sections}
          activeIndex={activeSection}
          onCategorySelect={handleSectionChange}
        />
      )}

      {/* 2. Sticky category tabs — only when multiple sections */}
      {entry.sections.length > 1 && (
        <StickyTabsWithSections
          entry={entry}
          activeSection={activeSection}
          onSectionChange={handleSectionChange}
        />
      )}

      {/* Single-section fallback (no tabs) */}
      {entry.sections.length <= 1 && (
        <>
          <TrendIntro
            title={entry.introTitle}
            themes={entry.themes}
            paragraphs={entry.introText}
            quote={entry.quote}
            readMoreLabel={t("trend_read_more", "Read more")}
            readLessLabel={t("trend_read_less", "Read less")}
          />
          {entry.sections.map((section, i) => (
            <TrendSection
              key={section.title}
              title={section.title}
              subtitle={section.subtitle}
              index={i}
            >
              <TrendCardGrid
                cards={section.cards}
                trendSlug={entry.slug}
                disableNavigation={Object.keys(entry.cardDetails).length === 0}
                expandLastRow={entry.sections.length <= 1}
              />
            </TrendSection>
          ))}
        </>
      )}
    </div>
  );
};

const StickyTabsWithSections = ({
  entry,
  activeSection,
  onSectionChange,
}: {
  entry: (typeof trendData)[string];
  activeSection: number;
  onSectionChange: (index: number) => void;
}) => {
  const { t } = useTranslation();

  const section = entry.sections[activeSection];

  return (
    <>
      <CategoryTabs
        sections={entry.sections}
        activeIndex={activeSection}
        onTabClick={onSectionChange}
      />

      <TrendIntro
        themes={section?.themes ?? entry.themes}
        paragraphs={section?.introText ?? entry.introText}
        quote={section?.quote ?? entry.quote}
        readMoreLabel={t("trend_read_more", "Read more")}
        readLessLabel={t("trend_read_less", "Read less")}
      />

      {section && (
        <TrendSection
          key={section.title}
          title={section.title}
          subtitle={section.subtitle}
          index={activeSection}
        >
          <TrendCardGrid
            cards={section.cards}
            trendSlug={entry.slug}
            disableNavigation={Object.keys(entry.cardDetails).length === 0}
            expandLastRow={entry.sections.length <= 1}
          />
        </TrendSection>
      )}
    </>
  );
};
